import {
  isAnswerObservationsResult,
  type AnswerMap,
  type AnswerObservationsResult,
  type QuizDefinition,
  type QuizResult,
  type ScoreResult
} from "@quiz/quiz-core";
import { getQuizPresentation } from "@quiz/quiz-definitions";
import type { LeadResponse } from "../api/leadApi";
import { renderLeadForm, type LeadFormOptions } from "./LeadScreen";
import type { ResolvedUiCopy } from "../ui/copy";
import { button, el } from "../utils";
import { ArrowRight, CheckCircle2, createElement, LockKeyhole, MapPinned, XCircle } from "lucide";
import { renderCampaignMedia } from "../components/CampaignMedia";

export interface ResultScreenOptions {
  quiz: QuizDefinition;
  result: QuizResult;
  answers?: AnswerMap;
  copy: ResolvedUiCopy;
  leadResponse?: LeadResponse;
  leadFormOptions?: LeadFormOptions;
  onLead: () => void;
}

export function renderResultScreen(options: ResultScreenOptions): HTMLElement {
  if (!options.leadResponse && getQuizPresentation(options.quiz).booking) return renderBookingCaptureScreen(options);
  if (isAnswerObservationsResult(options.result)) return renderObservationResultScreen(options, options.result);
  if (!options.leadResponse && getQuizPresentation(options.quiz).comicQuest) {
    return renderComicQuestResultScreen(options as ResultScreenOptions & { result: ScoreResult });
  }
  if (!options.leadResponse && getQuizPresentation(options.quiz).resultGate === "personal-plan") {
    return renderPersonalPlanLockedScreen(options as ResultScreenOptions & { result: ScoreResult });
  }

  const scoredOptions = options as ResultScreenOptions & { result: ScoreResult };
  const definition = options.quiz.results.find((candidate) => candidate.id === options.result.id)!;
  const screen = el("main", "screen result-screen");
  const shell = el("section", `result-shell${options.quiz.leadForm.placement === "inline-result" ? " result-shell--inline" : ""}`);
  const presentation = getQuizPresentation(options.quiz);
  const compact = !options.leadResponse && presentation.resultGate === "compact";
  if (compact) {
    screen.classList.add("result-screen--compact");
    shell.classList.add("result-shell--compact");
  }
  if (options.leadResponse) shell.classList.add("result-shell--unlocked");

  const copy = el("div", "result-copy");
  if (compact) {
    copy.classList.add("result-copy--locked");
    copy.append(el("h1", "result-title", options.copy.resultEyebrow));
    copy.append(renderLockedPreview(scoredOptions));
    copy.append(el("p", "result-capture-text", options.copy.lockedText));
  } else {
    copy.append(el("p", "eyebrow", options.copy.resultEyebrow));
    copy.append(el("h1", "result-title", definition.title));
    const match = el("div", "result-match");
    match.append(
      el("strong", "result-match__value", `${options.result.normalizedScores[definition.category] ?? 0}%`),
      el("span", "result-match__label", definition.scoreLabel ?? definition.shortTitle ?? definition.title)
    );
    copy.append(match);
    copy.append(el("p", "result-description", definition.description));
    if (options.leadResponse) copy.append(el("p", "result-preview", definition.preview));
    if (options.leadResponse) copy.append(renderRankedBreakdown(options.quiz, options.result, options.copy));

    const gated = el("div", `gated-result${options.leadResponse ? " is-open" : ""}`);
    if (!options.leadResponse) gated.append(renderLockedPreview(scoredOptions));
    gated.append(el("h2", "gated-result__title", options.leadResponse ? options.copy.fullResultTitle : options.copy.lockedTitle));
    gated.append(el("p", "gated-result__text", options.leadResponse ? definition.gatedContent : options.copy.lockedText));
    copy.append(gated);
  }

  const successVisual = options.leadResponse ? presentation.mascot?.success : undefined;
  const visual = successVisual
    ? { ...successVisual, placement: options.quiz.resultPresentation?.visual?.placement ?? "card-overlap-top", align: "center" as const, size: "small" as const }
    : options.quiz.resultPresentation?.visual;
  const media = renderCampaignMedia(visual, "result-media");
  const story = el("div", "result-story");
  if (media && visual) {
    story.classList.add(`result-story--${visual.placement}`);
    media.dataset.align = visual.align ?? "center";
    media.dataset.size = visual.size ?? "medium";
    if (visual.placement === "heading") copy.prepend(media);
    else story.append(media);
    shell.classList.add("result-shell--media");
  }
  story.append(copy);

  if (options.quiz.leadForm.placement === "inline-result" && options.leadFormOptions && !options.leadResponse) {
    const inline = el("aside", "inline-lead-card");
    if (!compact) inline.append(el("p", "eyebrow", options.copy.fullResultTitle));
    inline.append(el("h2", "inline-lead-card__title", options.quiz.leadForm.title));
    if (options.quiz.leadForm.subtitle) inline.append(el("p", "inline-lead-card__subtitle", options.quiz.leadForm.subtitle));
    inline.append(renderLeadForm(options.leadFormOptions));
    shell.append(story, inline);
  } else {
    if (options.quiz.leadForm.placement !== "inline-result" && !options.leadResponse) {
      copy.append(button("primary-button", options.copy.resultCta, options.onLead));
    }
    if (definition.illustration && !media && !compact) {
      const image = el("img", "result-image") as HTMLImageElement;
      image.src = definition.illustration;
      image.alt = definition.title;
      image.loading = "lazy";
      shell.append(story, image);
    } else {
      shell.append(story);
    }
  }

  screen.append(shell);
  return screen;
}

function renderBookingCaptureScreen(options: ResultScreenOptions): HTMLElement {
  const booking = getQuizPresentation(options.quiz).booking!;
  const screen = el("main", "screen result-screen booking-result-screen");
  const shell = el("section", "booking-result");
  shell.append(el("p", "booking-result__eyebrow", "Майже готово"));
  shell.append(el("h1", "booking-result__title", booking.title));
  const summary = el("div", "booking-result__summary");
  for (const [questionId, label, icon] of [
    [booking.goalQuestionId, booking.goalLabel, "🎯"],
    [booking.blockerQuestionId, booking.blockerLabel, "💬"]
  ]) {
    const answerId = options.answers?.[questionId];
    const answer = options.quiz.questions.find((question) => question.id === questionId)?.answers.find((item) => item.id === answerId);
    if (!answer) continue;
    const row = el("p", "booking-result__summary-row");
    row.append(el("span", "booking-result__summary-icon", icon), el("span", "booking-result__summary-label", `${label}:`), el("strong", "booking-result__summary-value", answer.label));
    summary.append(row);
  }
  shell.append(summary, el("p", "booking-result__intro", booking.introduction));
  const capture = el("section", "booking-result__capture");
  capture.append(el("h2", "booking-result__form-title", options.quiz.leadForm.title));
  if (options.leadFormOptions) capture.append(renderLeadForm(options.leadFormOptions));
  capture.append(el("p", "booking-result__after-note", booking.afterFormNote));
  shell.append(capture);
  screen.append(shell);
  return screen;
}

function renderComicQuestResultScreen(options: ResultScreenOptions & { result: ScoreResult }): HTMLElement {
  const presentation = getQuizPresentation(options.quiz);
  const comic = presentation.comicQuest!;
  const sceneCopy = comic.copy;
  const screen = el("main", "screen result-screen comic-result-screen");
  const shell = el("section", "comic-result comic-result--locked");
  const brand = renderComicResultBrand(options.quiz, sceneCopy?.readyChapter ?? "Фінал · Блеквуд");
  const visual = el("div", "comic-result__visual comic-result__visual--locked");
  const art = renderCampaignMedia(comic.lockedResultArt, "comic-result__art");
  if (art) visual.append(art);
  visual.append(brand);

  const hero = el("div", "comic-result__hero");
  hero.append(el("p", "comic-result__eyebrow", options.copy.resultEyebrow));
  hero.append(el("h1", "comic-result__title", sceneCopy?.readyTitle ?? "Твій розбір готовий"));
  if (sceneCopy?.readyIntro) hero.append(el("p", "comic-result__ready-intro", sceneCopy.readyIntro));
  const locked = el("div", "comic-result__locked-preview");
  const lock = el("span", "comic-result__locked-icon");
  lock.setAttribute("aria-hidden", "true");
  lock.append(createElement(LockKeyhole, { width: 24, height: 24, "stroke-width": 1.8 }));
  const lockedRows = el("div", "comic-result__locked-rows");
  const rows = sceneCopy?.readyRows ?? [`${options.quiz.questions.length} відповідей перевірено`, "Результат і рівень пораховано", "Розбір помилок підготовлено"];
  lockedRows.append(...rows.map((label, index) => lockedResultRow(label, index === 0)));
  locked.append(lock, lockedRows);
  hero.append(locked);
  hero.append(el("p", "comic-result__access-note", sceneCopy?.accessNote ?? "Результат, помилки та персональний план відкриються після короткої форми."));
  const cta = button("primary-button comic-result__cta", options.copy.resultCta, options.onLead);
  cta.append(createElement(ArrowRight, { width: 20, height: 20, "aria-hidden": "true" }));
  hero.append(cta);

  shell.append(visual, hero);
  screen.append(shell);
  return screen;
}

export function renderComicQuestUnlockedResultScreen(options: {
  quiz: QuizDefinition;
  result: ScoreResult;
  answers: AnswerMap;
  copy: ResolvedUiCopy;
  response: LeadResponse;
  onExternalCtaClick?: () => void;
}): HTMLElement {
  const comic = getQuizPresentation(options.quiz).comicQuest!;
  const sceneCopy = comic.copy;
  const mistakes = options.quiz.questions.flatMap((question, index) => {
    const correct = question.answers.find((answer) => answer.tags?.includes("correct"));
    const selected = question.answers.find((answer) => answer.id === options.answers[question.id]);
    if (!correct || selected?.id === correct.id) return [];
    const sceneTitle = question.visual?.alt?.split(":")[0] ?? `Сцена ${index + 1}`;
    return [{ question, correct, selected, index, sceneTitle }];
  });
  const total = options.quiz.questions.length;
  const rawScore = options.result.rawScores.grammar;
  const correctCount = Number.isFinite(rawScore)
    ? Math.max(0, Math.min(total, Math.round(rawScore)))
    : total - mistakes.length;
  const percent = Math.round((correctCount / total) * 100);

  const screen = el("main", "screen success-screen comic-result-screen");
  const shell = el("section", "comic-result comic-result--unlocked");
  const brand = renderComicResultBrand(options.quiz, "Результат відкрито");
  const visual = el("div", "comic-result__visual comic-result__visual--unlocked");
  const art = renderCampaignMedia(comic.unlockedResultArt, "comic-result__art");
  if (art) visual.append(art);
  visual.append(brand);
  const hero = el("div", "comic-result__hero");
  hero.append(el("p", "comic-result__eyebrow", options.response.delivery_state === "failed" ? options.copy.leadSavedLocal : options.copy.leadSaved));
  hero.append(el("h1", "comic-result__title", sceneCopy?.unlockedTitle ?? "Ти вибрався з Блеквуда"));
  if (sceneCopy?.unlockedIntro) hero.append(el("p", "comic-result__ready-intro", sceneCopy.unlockedIntro));
  const score = el("div", "comic-result__score");
  score.append(
    el("strong", "comic-result__score-value", `${correctCount}/${total}`),
    el("span", "comic-result__score-copy", `${scoreTier(correctCount, total)}\n${mistakes.length === 0 ? "Жодної помилки" : `${mistakes.length} ${mistakeWord(mistakes.length)}`}`)
  );
  const meter = el("div", "comic-result__meter");
  const meterBar = el("span", "comic-result__meter-bar");
  meterBar.style.width = `${percent}%`;
  meter.append(meterBar);
  hero.append(score, meter);
  if (comic.finalStoryBeat) hero.append(el("p", "comic-result__story", comic.finalStoryBeat));

  const review = renderComicMistakeReview(options.quiz, mistakes);
  const capture = el("section", "comic-result__capture");
  capture.dataset.stamp = sceneCopy?.captureStamp ?? "BLACKWOOD ROUTE";
  const captureMark = el("span", "comic-result__capture-mark");
  captureMark.setAttribute("aria-hidden", "true");
  captureMark.append(createElement(MapPinned, { width: 23, height: 23, "stroke-width": 1.8 }));
  const captureCopy = el("div", "comic-result__capture-copy");
  captureCopy.append(
    el("p", "comic-result__capture-eyebrow", sceneCopy?.captureEyebrow ?? "Твій маршрут далі"),
    el("h2", "comic-result__capture-title", sceneCopy?.captureTitle ?? "Забери персональний план навчання"),
    el("p", "comic-result__capture-text", sceneCopy?.captureText ?? "Арчі вже позначив теми, з яких варто почати, і наступні кроки без зайвого хаосу.")
  );
  if (comic.topics && comic.showTopicsInResult !== false) {
    const topics = [...new Set(mistakes.map(({ question }) => comic.topics?.[question.id]).filter((topic): topic is string => Boolean(topic)))];
    if (topics.length > 0) {
      const list = el("ul", "comic-result__topics");
      list.append(...topics.map((topic) => el("li", "", topic)));
      captureCopy.append(list);
    }
  }
  const completion = getQuizPresentation(options.quiz).completion!;
  const cta = el("a", "primary-button comic-result__cta telegram-plan-cta", completion.ctaLabel) as HTMLAnchorElement;
  cta.href = completion.ctaUrl;
  cta.target = "_blank";
  cta.rel = "noopener noreferrer";
  cta.append(createElement(ArrowRight, { width: 20, height: 20, "aria-hidden": "true" }));
  cta.addEventListener("click", () => options.onExternalCtaClick?.());
  capture.append(captureMark, captureCopy, cta);
  shell.append(visual, hero, capture, review);
  screen.append(shell);
  return screen;
}

function renderComicResultBrand(quiz: QuizDefinition, chapter: string): HTMLElement {
  const comic = getQuizPresentation(quiz).comicQuest!;
  const brand = el("header", "comic-result__brand");
  const logo = el("img", "comic-result__logo") as HTMLImageElement;
  logo.src = comic.logoSrc;
  logo.alt = quiz.metadata.name;
  brand.append(logo, el("span", "comic-result__chapter", chapter));
  return brand;
}

function lockedResultRow(label: string, complete: boolean): HTMLElement {
  const row = el("div", `comic-result__locked-row${complete ? " is-complete" : ""}`);
  const icon = el("span", "comic-result__locked-row-icon");
  icon.setAttribute("aria-hidden", "true");
  icon.append(complete
    ? createElement(CheckCircle2, { width: 17, height: 17, "stroke-width": 2 })
    : createElement(LockKeyhole, { width: 16, height: 16, "stroke-width": 1.8 }));
  row.append(icon, el("span", "", label));
  return row;
}

function renderComicMistakeReview(
  quiz: QuizDefinition,
  mistakes: Array<{
    question: QuizDefinition["questions"][number];
    correct: QuizDefinition["questions"][number]["answers"][number];
    selected: QuizDefinition["questions"][number]["answers"][number] | undefined;
    index: number;
    sceneTitle: string;
  }>
): HTMLElement {
  const review = el("section", "comic-result__review");
  review.append(el("h2", "comic-result__review-title", mistakes.length === 0 ? "Усі пастки пройдено" : "Де були помилки"));
  if (mistakes.length === 0) {
    const perfect = el("div", "comic-result__perfect");
    perfect.append(
      createElement(CheckCircle2, { width: 22, height: 22, "aria-hidden": "true" }),
      el("p", "", getQuizPresentation(quiz).comicQuest?.copy?.perfectText ?? `Ти правильно відповів на всі ${quiz.questions.length} запитань. Арчі схвально мовчить.`)
    );
    review.append(perfect);
    return review;
  }
  const list = el("div", "comic-result__mistakes");
  list.append(...mistakes.map((mistake) => {
    const item = el("article", "comic-mistake");
    const heading = el("div", "comic-mistake__heading");
    const icon = el("span", "comic-mistake__icon");
    icon.setAttribute("aria-hidden", "true");
    icon.append(createElement(XCircle, { width: 18, height: 18 }));
    heading.append(icon, el("div", "", `Сцена ${String(mistake.index + 1).padStart(2, "0")} · ${mistake.sceneTitle}`));
    const comparison = el("div", "comic-mistake__comparison");
    comparison.append(
      answerLine("Твоя відповідь", mistake.selected?.label ?? "Немає відповіді", false),
      answerLine("Правильно", mistake.correct.label, true)
    );
    item.append(
      heading,
      el("p", "comic-mistake__prompt", mistake.question.title),
      comparison,
      el("p", "comic-mistake__explanation", getQuizPresentation(quiz).comicQuest?.explanations?.[mistake.question.id] ?? grammarExplanation(mistake.question.id))
    );
    return item;
  }));
  review.append(list);
  return review;
}

function answerLine(label: string, value: string, correct: boolean): HTMLElement {
  const line = el("div", `comic-mistake__answer${correct ? " is-correct" : ""}`);
  line.append(el("span", "", label), el("strong", "", value));
  return line;
}

function scoreTier(score: number, total: number): string {
  const ratio = score / total;
  if (ratio >= 0.85) return "Майже бездоганно";
  if (ratio >= 0.65) return "Впевнений мандрівник";
  if (ratio >= 0.4) return "Непоганий старт";
  return "Час підтягнути базу";
}

function mistakeWord(count: number): string {
  const lastTwo = count % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return "помилок";
  const last = count % 10;
  if (last === 1) return "помилка";
  if (last >= 2 && last <= 4) return "помилки";
  return "помилок";
}

function grammarExplanation(questionId: string): string {
  return grammarExplanations[questionId] ?? "У цьому реченні потрібна форма, яка узгоджується з контекстом історії.";
}

const grammarExplanations: Record<string, string> = {
  scene_01: "Past Continuous описує тривалу дію, яку перервала інша подія в минулому.",
  scene_02: "Займенник they у минулому часі вживається з формою were.",
  scene_03: "Past Simple показує завершену подію, що сталася в конкретний момент історії.",
  scene_04: "Після didn't використовується базова форма дієслова: have.",
  scene_05: "Неправильне дієслово hear у Past Simple має форму heard.",
  scene_06: "Mustn't уже є готовою заперечною формою модального дієслова і не потребує to.",
  scene_07: "Among означає «серед» кількох дерев або предметів.",
  scene_08: "On вживаємо, коли предмет лежить на поверхні столу.",
  scene_09: "У розповіді про минуле з підметом it потрібна форма was.",
  scene_10: "Past Continuous підкреслює дві паралельні дії, що тривали одночасно.",
  scene_11: "У минулому контексті неправильне дієслово keep змінюється на kept.",
  scene_12: "Archie — підмет в однині, тому в минулому часі потрібна форма was.",
  scene_13: "У Second Conditional після if ставимо Past Simple, а в результаті — would + дієслово.",
  scene_14: "Revolver and bullets — складений підмет у множині, тому потрібне were hanging.",
  scene_15: "Уявний наслідок у Second Conditional будується через would + дієслово.",
  scene_16: "Door — однина, а стан у минулому передається конструкцією was locked.",
  scene_17: "Was hidden — пасивна конструкція: люк був схований кимось раніше.",
  scene_18: "Карти висять на поверхні стін, тому використовуємо прийменник on.",
  scene_19: "У послідовності минулих подій після before тут потрібен Past Simple: got.",
  scene_20: "Неправильне дієслово become у Past Simple має форму became."
};

function renderPersonalPlanLockedScreen(options: ResultScreenOptions & { result: ScoreResult }): HTMLElement {
  const presentation = getQuizPresentation(options.quiz);
  const completion = presentation.completion;
  const screen = el("main", "screen result-screen result-screen--compact result-screen--personal-plan");
  const shell = el("section", "result-shell result-shell--compact result-shell--personal-plan");

  const story = el("div", "result-story");
  const copy = el("div", "result-copy result-copy--locked personal-plan-result");
  copy.append(el("p", "eyebrow", options.copy.resultEyebrow));
  copy.append(el("h1", "result-title", completion?.lockedTitle ?? options.copy.lockedTitle));
  copy.append(renderPersonalPlanPreview());
  copy.append(el("p", "result-capture-text", completion?.lockedText ?? options.copy.lockedText));
  story.append(copy);

  if (options.leadFormOptions) {
    const inline = el("aside", "inline-lead-card inline-lead-card--personal-plan");
    inline.append(el("h2", "inline-lead-card__title", options.quiz.leadForm.title));
    if (options.quiz.leadForm.subtitle) inline.append(el("p", "inline-lead-card__subtitle", options.quiz.leadForm.subtitle));
    inline.append(renderLeadForm(options.leadFormOptions));
    shell.append(story, inline);
  } else {
    copy.append(button("primary-button", options.copy.resultCta, options.onLead));
    shell.append(story);
  }

  screen.append(shell);
  return screen;
}

function renderPersonalPlanPreview(): HTMLElement {
  const preview = el("div", "personal-plan-preview");
  const rows = [
    "Рівень англійської враховано",
    "Ціль навчання визначено",
    "Темп і формат підібрано",
    "Ще один крок - заповнення форми"
  ];
  preview.append(
    ...rows.map((label, index) => {
      const row = el("div", `personal-plan-preview__row${index === rows.length - 1 ? " is-locked" : ""}`);
      const icon = el("span", "personal-plan-preview__icon");
      icon.setAttribute("aria-hidden", "true");
      icon.append(
        index === rows.length - 1
          ? createElement(LockKeyhole, { width: 16, height: 16, "stroke-width": 1.8 })
          : createElement(CheckCircle2, { width: 16, height: 16, "stroke-width": 2 })
      );
      row.append(icon, el("span", "personal-plan-preview__label", label));
      return row;
    })
  );
  return preview;
}

function renderObservationResultScreen(options: ResultScreenOptions, result: AnswerObservationsResult): HTMLElement {
  const config = options.quiz.personalizedResult;
  if (!config) throw new Error(`Missing personalized result config for quiz "${options.quiz.slug}"`);

  const screen = el("main", "screen result-screen result-screen--observations");
  const shell = el("section", `result-shell result-shell--observations${!options.leadResponse ? " result-shell--compact" : " result-shell--unlocked"}`);
  const presentation = getQuizPresentation(options.quiz);
  const compactFlow = presentation.flowLayout === "compact";

  const copy = el("div", "result-copy observation-result");
  if (!options.leadResponse) {
    copy.classList.add("result-copy--locked");
    copy.append(el("h1", "result-title", config.lockedTitle));
    copy.append(renderObservationLockedPreview(compactFlow));
    if (!compactFlow) copy.append(el("p", "result-capture-text", config.lockedText ?? options.copy.lockedText));
  } else {
    copy.append(el("p", "eyebrow", options.copy.resultEyebrow));
    copy.append(el("h1", "result-title", config.unlockedTitle));
    copy.append(el("p", "observation-result__intro", config.intro));
    const list = el("div", "observation-list");
    list.append(
      ...result.observations.map((observation, index) => {
        const item = el("article", "observation-card");
        item.append(el("span", "observation-card__index", String(index + 1).padStart(2, "0")));
        item.append(el("p", "observation-card__text", observation.text));
        return item;
      })
    );
    copy.append(list);
    copy.append(el("p", "observation-result__outro", config.outro));
  }

  const successVisual = options.leadResponse ? presentation.mascot?.success : undefined;
  const visual = successVisual
    ? { ...successVisual, placement: options.quiz.resultPresentation?.visual?.placement ?? "card-overlap-top", align: "center" as const, size: "small" as const }
    : options.quiz.resultPresentation?.visual;
  const media = renderCampaignMedia(compactFlow && options.leadResponse ? undefined : visual, "result-media");
  const story = el("div", "result-story");
  if (media && visual) {
    story.classList.add(`result-story--${visual.placement}`);
    media.dataset.align = visual.align ?? "center";
    media.dataset.size = visual.size ?? "medium";
    if (visual.placement === "heading") copy.prepend(media);
    else story.append(media);
    shell.classList.add("result-shell--media");
  }
  story.append(copy);

  if (options.quiz.leadForm.placement === "inline-result" && options.leadFormOptions && !options.leadResponse) {
    const inline = el("aside", "inline-lead-card");
    inline.append(el("h2", "inline-lead-card__title", options.quiz.leadForm.title));
    if (options.quiz.leadForm.subtitle) inline.append(el("p", "inline-lead-card__subtitle", options.quiz.leadForm.subtitle));
    inline.append(renderLeadForm(options.leadFormOptions));
    shell.append(story, inline);
  } else {
    if (options.quiz.leadForm.placement !== "inline-result" && !options.leadResponse) {
      copy.append(button("primary-button", options.copy.resultCta, options.onLead));
    }
    shell.append(story);
  }

  screen.append(shell);
  return screen;
}

function renderLockedPreview(options: ResultScreenOptions & { result: ScoreResult }): HTMLElement {
  // Only numeric scores reach the teaser DOM, never real labels or recommendations.
  const preview = el("div", "locked-preview");
  const rows = el("div", "locked-preview__rows");
  const scores = options.quiz.results.map((item) => options.result.normalizedScores[item.category] ?? 0)
    .sort((a, b) => b - a).slice(0, 4);
  for (const [index, score] of scores.entries()) {
    const row = el("div", `locked-preview__row${index === 0 ? " is-primary" : ""}`);
    const label = el("span", "locked-preview__label");
    label.setAttribute("aria-hidden", "true");
    row.append(label, el("strong", "locked-preview__score", `${score}%`));
    rows.append(row);
  }
  const badge = el("span", "locked-preview__badge");
  badge.setAttribute("aria-hidden", "true");
  badge.append(createElement(LockKeyhole, { width: 18, height: 18, "stroke-width": 1.8 }));
  preview.append(rows, badge);
  return preview;
}

function renderObservationLockedPreview(compactFlow: boolean): HTMLElement {
  const preview = el("div", "locked-preview locked-preview--observations");
  const rows = el("div", "locked-preview__rows");
  for (let index = 0; index < 5; index++) {
    const row = el("div", `locked-preview__row${index === 0 ? " is-primary" : ""}`);
    const number = el("span", "locked-preview__number", String(index + 1).padStart(2, "0"));
    number.setAttribute("aria-hidden", "true");
    const label = el("span", "locked-preview__label");
    label.setAttribute("aria-hidden", "true");
    row.append(number, label);
    if (compactFlow) {
      const lock = createElement(LockKeyhole, { width: 15, height: 15, "stroke-width": 1.6, "aria-hidden": "true" });
      lock.classList.add("locked-preview__lock");
      row.append(lock);
    }
    rows.append(row);
  }
  if (compactFlow) {
    preview.append(rows);
    return preview;
  }
  const badge = el("span", "locked-preview__badge");
  badge.setAttribute("aria-hidden", "true");
  badge.append(createElement(LockKeyhole, { width: 18, height: 18, "stroke-width": 1.8 }));
  preview.append(rows, badge);
  return preview;
}

function renderRankedBreakdown(quiz: QuizDefinition, result: ScoreResult, copy: ResolvedUiCopy): HTMLElement {
  const wrap = el("div", "ranked-results");
  wrap.append(el("h2", "ranked-results__title", copy.resultBreakdownTitle));
  const rows = quiz.results
    .map((definition) => ({
      definition,
      score: result.normalizedScores[definition.category] ?? 0
    }))
    .sort((a, b) => b.score - a.score);

  wrap.append(
    ...rows.map((row) => {
      const item = el("div", `ranked-result${row.definition.id === result.id ? " is-primary" : ""}`);
      const top = el("div", "ranked-result__top");
      top.append(el("span", "", row.definition.shortTitle ?? row.definition.title), el("strong", "", `${row.score}%`));
      const track = el("div", "ranked-result__track");
      const bar = el("div", "ranked-result__bar");
      bar.style.width = `${row.score}%`;
      track.append(bar);
      item.append(top, track);
      return item;
    })
  );
  return wrap;
}
